import React, { useState, useEffect } from 'react';
import { X, ChevronDown, ChevronUp, Info, Shield, Cookie } from 'lucide-react';

interface CookieSettings {
  essential: boolean; // Always true, cannot be changed
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
}

const CookieConsent: React.FC = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [cookieSettings, setCookieSettings] = useState<CookieSettings>({
    essential: true,
    analytics: true,
    marketing: true,
    preferences: true
  });

  useEffect(() => {
    // Check if user has already set cookie preferences
    const savedSettings = localStorage.getItem('cookieSettings');
    if (savedSettings) {
      setCookieSettings(JSON.parse(savedSettings));
    } else {
      // No preferences set, show the banner
      setShowBanner(true);
    }
  }, []);

  const acceptAllCookies = () => {
    const settings: CookieSettings = {
      essential: true,
      analytics: true,
      marketing: true,
      preferences: true
    };
    
    localStorage.setItem('cookieSettings', JSON.stringify(settings));
    localStorage.setItem('cookiesAccepted', 'true');
    setCookieSettings(settings);
    setShowBanner(false);
  };

  const acceptSelectedCookies = () => {
    // Essential cookies are always true
    const settings: CookieSettings = {
      ...cookieSettings,
      essential: true
    };
    
    localStorage.setItem('cookieSettings', JSON.stringify(settings));
    localStorage.setItem('cookiesAccepted', 'true');
    setShowBanner(false);
  };

  const handleCookieToggle = (type: keyof CookieSettings) => {
    // Don't allow toggling essential cookies
    if (type === 'essential') return;
    
    setCookieSettings(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white shadow-lg z-50 border-t border-gray-200">
      <div className="container mx-auto px-4 py-4">
        <div className="flex flex-col space-y-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between">
            <div className="flex items-start space-x-3">
              <Cookie className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <h3 className="text-base font-medium text-gray-900 mb-1">Cookie Preferences</h3>
                <p className="text-sm text-gray-600 max-w-2xl">
                  We use cookies to ensure you get the best experience on our website. Some cookies are essential for the website to function properly, while others help us improve your experience.
                </p>
              </div>
            </div>
            <div className="flex space-x-3 mt-4 sm:mt-0">
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="text-gray-600 hover:text-gray-800 text-sm font-medium flex items-center"
              >
                {showDetails ? (
                  <>
                    <ChevronUp className="h-4 w-4 mr-1" />
                    Hide Details
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4 mr-1" />
                    Customize
                  </>
                )}
              </button>
              <button
                onClick={acceptAllCookies}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
              >
                Accept All
              </button>
              <button
                onClick={acceptSelectedCookies}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors text-sm"
              >
                Accept Selected
              </button>
              <button
                onClick={() => setShowBanner(false)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
          
          {showDetails && (
            <div className="bg-gray-50 p-4 rounded-md mt-2">
              <h4 className="text-sm font-medium text-gray-900 mb-3">Cookie Settings</h4>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="essential"
                      type="checkbox"
                      checked={cookieSettings.essential}
                      disabled
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded cursor-not-allowed opacity-60"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="essential" className="font-medium text-gray-900 flex items-center">
                      Essential Cookies
                      <Shield className="h-4 w-4 ml-1 text-green-600" />
                    </label>
                    <p className="text-gray-500">
                      These cookies are necessary for the website to function properly and cannot be disabled.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="analytics"
                      type="checkbox"
                      checked={cookieSettings.analytics}
                      onChange={() => handleCookieToggle('analytics')}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="analytics" className="font-medium text-gray-900">Analytics Cookies</label>
                    <p className="text-gray-500">
                      These cookies help us understand how visitors interact with our website, helping us improve our services.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="marketing"
                      type="checkbox"
                      checked={cookieSettings.marketing}
                      onChange={() => handleCookieToggle('marketing')}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="marketing" className="font-medium text-gray-900">Marketing Cookies</label>
                    <p className="text-gray-500">
                      These cookies are used to track visitors across websites to display relevant advertisements.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="preferences"
                      type="checkbox"
                      checked={cookieSettings.preferences}
                      onChange={() => handleCookieToggle('preferences')}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="preferences" className="font-medium text-gray-900">Preference Cookies</label>
                    <p className="text-gray-500">
                      These cookies enable personalized features and remember your preferences across our website.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between items-center">
                <div className="flex items-center text-sm text-gray-500">
                  <Info className="h-4 w-4 mr-1" />
                  <span>You can change these settings at any time in our Privacy Policy.</span>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={acceptSelectedCookies}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
                  >
                    Save Preferences
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;